import React, { useEffect, useMemo, useState } from "react";
import { collection, addDoc, getDocs, query, where, doc, deleteDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { getCurrentUser } from "../../auth";

export default function ClaimsManager() {
  const me = getCurrentUser();
  const role = String(me?.role || "").toLowerCase();
  const isAdmin = role === "admin" || role === "superadmin";

  const [uid, setUid] = useState("");
  const [email, setEmail] = useState("");
  const [campus, setCampus] = useState("gapan");
  const [roles, setRoles] = useState(["registrar"]);
  const [requests, setRequests] = useState([]);
  const [info, setInfo] = useState("");

  const roleOptions = useMemo(() => ["superadmin","admin","registrar","encoder","teacher","finance","readonly"], []);

  async function load() {
    let q = collection(db, "claims_requests");
    if (!isAdmin) q = query(q, where("requestedBy", "==", me?.email || ""));
    const snap = await getDocs(q);
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a,b)=>String(b.createdAt||"").localeCompare(String(a.createdAt||"")));
    setRequests(rows);
  }
  useEffect(() => { load(); }, []);

  function buildCommand() {
    const rolesCSV = roles.join(",");
    const campusArg = roles.includes("superadmin") ? "all" : campus;
    return `export GOOGLE_APPLICATION_CREDENTIALS="/path/to/service-account.json"\nnode scripts/sis_set_claims.mjs ${uid} "${rolesCSV}" ${campusArg}`;
  }

  async function submit() {
    if (!uid || roles.length === 0) { setInfo("UID and at least one role are required."); return; }
    const payload = {
      uid,
      email: email || "",
      roles,
      campus: roles.includes("superadmin") ? "all" : campus,
      requestedBy: me?.email || "",
      createdAt: new Date().toISOString(),
      status: "pending"
    };
    try {
      await addDoc(collection(db, "claims_requests"), payload);
      setInfo("Request saved. If Cloud Functions are deployed, claims will be applied automatically.");
      await load();
    } catch (e) {
      setInfo("Failed to save request.");
    }
  }

  async function remove(id) {
    await deleteDoc(doc(db, "claims_requests", id));
    await load();
  }

  return (
    <div className="container mt-4">
      <h3 className="mb-3">Claims Manager</h3>
      <div className="card p-3 mb-3">
        <div className="row g-2">
          <div className="col-md-3">
            <label className="form-label">UID</label>
            <input className="form-control" value={uid} onChange={e => setUid(e.target.value)} placeholder="Firebase UID" />
          </div>
          <div className="col-md-3">
            <label className="form-label">Email (optional)</label>
            <input className="form-control" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Campus</label>
            <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)}>
              <option value="gapan">Gapan</option>
              <option value="bulacan">Bulacan</option>
              <option value="all">All (for superadmin)</option>
            </select>
          </div>
          <div className="col-12">
            <label className="form-label">Roles</label>
            <div className="d-flex flex-wrap gap-3">
              {roleOptions.map(r => (
                <label key={r} className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={roles.includes(r)}
                    onChange={e => {
                      setRoles(prev => e.target.checked ? Array.from(new Set(prev.concat([r]))) : prev.filter(x => x !== r));
                    }}
                  />
                  <span className="ms-1 text-capitalize">{r}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="col-12 d-flex gap-2">
            <button className="btn btn-primary" onClick={submit}>Save Request</button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                const cmd = buildCommand();
                navigator.clipboard.writeText(cmd).then(() => setInfo("Command copied to clipboard. Paste into your terminal.")); 
              }}
            >
              Copy Terminal Command
            </button>
          </div>
          {info && <div className="mt-2 alert alert-info">{info}</div>}
          <div className="mt-3">
            <div className="small text-muted">
              Note: Applying claims requires Firebase Admin privileges. If you deploy a Cloud Function to process these requests,
              entries will move from "pending" to "applied" automatically. Otherwise, use the copied terminal command.
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light"><tr><th>UID</th><th>Email</th><th>Roles</th><th>Campus</th><th>Status</th><th>Requested By</th><th></th></tr></thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id}>
                  <td>{r.uid}</td>
                  <td>{r.email || ""}</td>
                  <td>{Array.isArray(r.roles) ? r.roles.join(", ") : ""}</td>
                  <td className="text-capitalize">{r.campus || ""}</td>
                  <td>{r.status || "pending"}</td>
                  <td>{r.requestedBy || ""}</td>
                  <td className="text-end"><button className="btn btn-sm btn-outline-danger" onClick={() => remove(r.id)}>Delete</button></td>
                </tr>
              ))}
              {requests.length === 0 && <tr><td colSpan="7" className="text-center text-muted">No requests</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
